import BaseStore from '@/store/BaseStore'
import { Action, Module, Mutation } from 'vuex-module-decorators'
import {
  LendCollectionInfo,
  defaultLendCollectionInfo,
  ReturnCollection,
  lendApi,
  userApi,
  LendAppendixRequest,
  ReturnAppendixRequest
} from '@/api'
// @ts-ignore
import { ReturnCollectionTableItem } from '@/components/templates/return/ReturnCollectionTable.vue'
// @ts-ignore
import { ReturnBorrowCollectionTableItem } from '@/components/templates/return/ReturnBorrowCollectionTable.vue'
import { cloneDeep } from 'lodash'
import { systemCodeStore } from '@/utils/store-accessor'
import { CollectionStatus } from '@/types'

@Module({
  name: 'ReturnPageStore',
  namespaced: true,
  stateFactory: true
})
export default class ReturnPageStore extends BaseStore {
  // 返却資料
  returnCollectionTableItems: ReturnCollectionTableItem[] = []

  // インデックスでないとメモ更新時のチェックボックス保持でどうしても都合が悪いのでこちらで対応
  selectedCollectionsOfReturnIndex: number[] = []

  // 貸借返却資料
  returnBorrowCollectionTableItems: ReturnBorrowCollectionTableItem[] = []

  // 貸出中資料情報
  infoOfCollectionsOfLendOut: LendCollectionInfo = defaultLendCollectionInfo()

  shelfType: string = ''

  handlingType: string = ''

  _deleteHandlingType: boolean = false

  returnInfoActiveToggleValue: 'collection' | 'borrow' = 'collection'

  lendInfoActiveToggleValue: 'show' | 'hide' = 'show'

  isContinuousReading: boolean = false

  readCollectionCount: number = 0

  get getReturnCollectionTableItems(): ReturnCollectionTableItem[] {
    return this.returnCollectionTableItems
  }

  get displayReturnCollectionTableItems(): ReturnCollectionTableItem[] {
    return this.returnCollectionTableItems.filter(
      value => value.collectionStatus.code !== CollectionStatus.Composed
    )
  }

  get getSelectedCollectionsOfReturnIndex(): number[] {
    return this.selectedCollectionsOfReturnIndex
  }

  get getReturnBorrowCollectionTableItems(): ReturnBorrowCollectionTableItem[] {
    return this.returnBorrowCollectionTableItems
  }

  get getInfoOfCollectionsOfLendOut(): LendCollectionInfo {
    return this.infoOfCollectionsOfLendOut
  }

  get getShelfType(): string {
    return this.shelfType
  }

  get getHandlingType(): string {
    return this.handlingType
  }

  get getDeleteHandlingType(): boolean {
    return this._deleteHandlingType
  }

  get getReturnInfoActiveToggleValue(): 'collection' | 'borrow' {
    return this.returnInfoActiveToggleValue
  }

  get getLendInfoActiveToggleValue(): 'show' | 'hide' {
    return this.lendInfoActiveToggleValue
  }

  get getIsContinuousReading(): boolean {
    return this.isContinuousReading
  }

  get getReadCollectionCount(): number {
    return this.readCollectionCount
  }

  @Mutation
  public initializeInputtedItem() {
    if (systemCodeStore.isClearReturnPageHandlingType) {
      this.handlingType = ''
    }
    if (systemCodeStore.isClearReturnPageShelfType) {
      this.shelfType = ''
    }
    this._deleteHandlingType = false
    this.returnInfoActiveToggleValue = 'collection'
    this.isContinuousReading = false
    this.readCollectionCount = 0
  }

  @Mutation
  public pushReturnCollectionTableItems(array: ReturnCollectionTableItem[]) {
    this.returnCollectionTableItems.push(...array)
  }

  @Mutation
  public pushReturnBorrowCollectionTableItems(
    array: ReturnBorrowCollectionTableItem[]
  ) {
    this.returnBorrowCollectionTableItems.push(...array)
  }

  @Mutation
  public setReturnCollectionTableItems(array: ReturnCollectionTableItem[]) {
    this.returnCollectionTableItems = array
  }

  @Mutation
  public deleteReturnCollectionTableItem(obj: {
    id: number
    deleteCount?: number
  }) {
    const findIndex = this.returnCollectionTableItems.findIndex(
      value => value.id === obj.id
    )

    const parentId: number = this.returnCollectionTableItems[findIndex].id
    this.returnCollectionTableItems.splice(findIndex, obj.deleteCount)
    this.returnCollectionTableItems = this.returnCollectionTableItems.filter(
      value => value.parentComposedCollectionId !== parentId
    )
  }

  @Mutation
  public setSelectedCollectionsOfReturnIndex(array: number[]) {
    this.selectedCollectionsOfReturnIndex = array
  }

  @Mutation
  public setReturnBorrowCollectionTableItems(
    array: ReturnBorrowCollectionTableItem[]
  ) {
    this.returnBorrowCollectionTableItems = array
  }

  @Mutation
  public setInfoOfCollectionsOfLendOut(value: LendCollectionInfo) {
    this.infoOfCollectionsOfLendOut = value
  }

  @Mutation
  public setShelfType(value: string) {
    this.shelfType = value
  }

  @Mutation
  public setHandlingType(value: string) {
    this.handlingType = value
  }

  @Mutation
  public setDeleteHandlingType(value: boolean) {
    this._deleteHandlingType = value
  }

  @Mutation
  public setReturnInfoActiveToggleValue(value: 'collection' | 'borrow') {
    this.returnInfoActiveToggleValue = value
  }

  @Mutation
  public setLendInfoActiveToggleValue(value: 'show' | 'hide') {
    this.lendInfoActiveToggleValue = value
  }

  @Mutation
  public setIsContinuousReading(value: boolean) {
    this.isContinuousReading = value
  }

  @Mutation
  public setReadCollectionCount(value: number) {
    this.readCollectionCount = value
  }

  @Action({ rawError: true })
  public async lendAppendix(request: LendAppendixRequest): Promise<void> {
    await lendApi.lendAppendix(request)
    await this.reconfigureListData(request.collectionId)
  }

  @Action({ rawError: true })
  public async returnAppendix(request: ReturnAppendixRequest): Promise<void> {
    await lendApi.returnAppendix(request)
    await this.reconfigureListData(request.collectionId)
  }

  // 返却一覧と貸出中一覧を最新のデータに調整する
  @Action({ rawError: true })
  public async reconfigureListData(collectionId: number): Promise<void> {
    const response: ReturnCollection = await lendApi.getReturnCollection({
      collectionId,
      collectionNo: '',
      assignedReserveIds: []
    })

    const tempTableItem = cloneDeep(this.returnCollectionTableItems)

    tempTableItem.forEach(tableItem => {
      if (tableItem.id === response.id) {
        if (tableItem.isHistory) {
          tableItem.appendixLendFlag = response.appendixLendFlag
          tableItem.checkAppendixLendFlag = response.appendixLendFlag
          tableItem.workMemo = response.workMemo
          tableItem.displayMemo = response.displayMemo
          tableItem.localMemo = response.localMemo
          tableItem.revision = response.revision
        } else {
          const tempItem: ReturnCollectionTableItem = cloneDeep(tableItem)
          Object.assign(tableItem, response)
          tableItem.checkAppendixLendFlag = response.appendixLendFlag
          tableItem.reservedId = tempItem.reservedId
          tableItem.reservedUserId = tempItem.reservedUserId
          tableItem.inputHandlingType = tempItem.inputHandlingType
          tableItem.inputShelfType = tempItem.inputShelfType
          tableItem._deleteHandlingType = tempItem._deleteHandlingType
        }
      }
    })

    this.setReturnCollectionTableItems(tempTableItem)

    if (this.infoOfCollectionsOfLendOut.userId !== '') {
      await this.getLendCollections(this.infoOfCollectionsOfLendOut.userId)
    }
  }

  @Action({ rawError: true })
  public async getLendCollections(userId: string): Promise<void> {
    const item: LendCollectionInfo = await userApi.getUserLendCollectionsById(
      userId
    )

    this.setInfoOfCollectionsOfLendOut({
      userId: item.userId,
      userName: item.userName,
      userType: item.userType,
      userRevision: item.userRevision,
      collections: item.collections.filter(collection =>
        this.returnCollectionTableItems
          .filter(item => !item.isHistory)
          .every(item => item.id !== collection.id)
      )
    })
  }
}
